import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import Cropper, { ReactCropperElement } from "react-cropper";
import { Icon } from "design-system/components/icon/icon";
import {
  CropContainer,
  ImageArea,
  ToolButton,
  ToolGroup
} from "./practice-crop.styles";

export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PracticeCropProps {
  image: string;
  onCropChange: (cropArea: CropArea) => void;
}

export interface PracticeCropRef {
  getCroppedImage: () => string | null;
}

export const PracticeCrop = forwardRef<PracticeCropRef, PracticeCropProps>(
  ({ image, onCropChange }, ref) => {
    const cropperRef = useRef<ReactCropperElement>(null);
    const [isCropMode, setIsCropMode] = useState(false);

    const reportCropChange = () => {
      const cropArea = cropperRef.current?.cropper.getData(true);

      if (!cropArea) return;

      onCropChange(cropArea);
    };

    const toggleCropMode = () => {
      const cropper = cropperRef.current?.cropper;

      if (!cropper) return;

      if (isCropMode) {
        cropper.clear();
        cropper.setDragMode("move");
      } else {
        cropper.crop();
        cropper.setDragMode("crop");
      }

      setIsCropMode((currentValue) => !currentValue);
    };

    const flipImage = () => {
      const cropper = cropperRef.current?.cropper;

      if (!cropper) return;

      const { scaleX } = cropper.getData();

      cropper.scaleX(scaleX === -1 ? 1 : -1);
      reportCropChange();
    };

    const getCroppedImage = () => {
      const cropper = cropperRef.current?.cropper;

      if (!cropper) return isCropMode ? null : image;

      const { scaleX, scaleY } = cropper.getData();
      const hasImageTransform = scaleX !== 1 || scaleY !== 1;

      if (!isCropMode && !hasImageTransform) return image;

      const canvas = cropper.getCroppedCanvas({
        fillColor: "#ffffff",
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high"
      });

      if (!canvas) return null;

      return canvas.toDataURL("image/jpeg", 0.9);
    };

    useImperativeHandle(ref, () => ({
      getCroppedImage
    }));

    return (
      <CropContainer>
        <ImageArea $isCropMode={isCropMode}>
          <Cropper
            ref={cropperRef}
            src={image}
            alt="Jawaban yang akan dipotong"
            style={{ height: "100%", width: "100%" }}
            viewMode={0}
            dragMode="move"
            autoCrop={false}
            autoCropArea={0.8}
            background={false}
            guides={false}
            center={false}
            highlight={false}
            responsive
            crop={reportCropChange}
          />
          <ToolGroup>
            <ToolButton
              type="button"
              aria-label="Potong gambar"
              aria-pressed={isCropMode}
              onClick={toggleCropMode}
            >
              <Icon name="general-crop" />
            </ToolButton>
            <ToolButton
              type="button"
              aria-label="Balik gambar"
              onClick={flipImage}
            >
              <Icon name="general-flip" />
            </ToolButton>
          </ToolGroup>
        </ImageArea>
      </CropContainer>
    );
  }
);

PracticeCrop.displayName = "PracticeCrop";
